// ABOUTME: Puts the GraphQL operation name in the request URL so telemetry can see it
// ABOUTME: Every operation POSTs to one path, which makes slow requests unattributable
import type { ApolloLink } from "@apollo/client"

/**
 * Appends `?op=<operationName>` to the endpoint.
 *
 * Every GraphQL request is a POST to the same path, so a slow one cannot be
 * tied to an operation: in a HAR they are told apart only by opening each
 * request body, and the API sends no server-side spans at all. That is why
 * "which resolver is slow?" had no answer.
 *
 * WHERE TO LOOK once this ships: Sentry strips the query string from the span
 * NAME — `getSanitizedUrlStringFromUrlObject` sets `search = ''` — so every
 * description still reads `POST …/graphql` and nothing looks different at
 * first glance. The name lands in the span ATTRIBUTES instead: `http.query`,
 * `http.url`, `url`. Group by `http.query` in the trace explorer. (Making the
 * descriptions differ too would be a `beforeSendSpan` that renames from
 * `http.query`, and would not need this URL change at all.)
 *
 * A query string is ignored by the GraphQL server and by the ingress, which
 * routes on path — and `resolveAppServiceUrl` rewrites the endpoint onto the
 * app's own origin in every deployed environment, so this adds no CORS
 * preflight there. Local dev is cross-origin and does get one OPTIONS per
 * distinct operation name, since the preflight cache keys on the full URL.
 *
 * Anonymous operations keep the bare endpoint rather than growing an `op=`
 * with nothing after it.
 *
 * The separator is chosen, not assumed: the endpoint comes from env through
 * resolveAppServiceUrl, which keeps any query string it was given, and a second
 * `?` would fold `op=` into the value of whatever parameter came first.
 */
export const withOperationName = (uri: string, operationName: string | undefined | null): string =>
  operationName ? `${uri}${uri.includes("?") ? "&" : "?"}op=${encodeURIComponent(operationName)}` : uri

export const operationURI =
  (uri: string) =>
  (operation: ApolloLink.Operation): string =>
    withOperationName(uri, operation.operationName)
